import { useEffect, useRef, useState } from "react";
import { useUnmount } from "react-use";
import * as P2PUtils from "@classwize/p2p-utils";
import { datadogLogs } from "@datadog/browser-logs";
import useStore from "@/stores/index";
import { useP2PContextStore } from "@/stores/p2pContextStore";
import p2pManager from "@/js/utils/p2pManager";
import { getClassroomIdForChat, isChatFeatureEnabled } from "@/js/utils/chatUtils";

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useP2p(open, peerAgentId, peer, peerType, onFallback) {
	const [hasSentInitEvent, setHasSentInitEvent] = useState(false);
	const [connectionStartTime, setConnectionStartTime] = useState(0);
	const [error, setError] = useState(null);

	const p2pContext = useP2PContextStore();

	const [peerConnecion, setPeerConnection] = useState(null);
	const peerConnectionRef = useRef();

	const peerConnectionCloseRef = useRef();
	const fallbackTimeoutRef = useRef();

	const statsDatachannelCloseRef = useRef();
	const statsIntervalRef = useRef();
	const studentSignallingEstablishedRef = useRef(false);

	let logPrefix = "";
	if (peerType === "laptop") {
		logPrefix = "Agent";
	} else if (peerType === "chromebook") {
		logPrefix = "Extension";
	} else {
		logPrefix = "unknown agent type";
	}

	// close p2p resources on unmount
	useUnmount(() => {
		console.debug("useP2p unmount - running cleanup");
		if (statsDatachannelCloseRef.current) {
			statsDatachannelCloseRef.current();
		}

		if (peerConnectionCloseRef.current) {
			peerConnectionCloseRef.current();
		}

		if (fallbackTimeoutRef.current) {
			clearTimeout(fallbackTimeoutRef.current);
			fallbackTimeoutRef.current = null;
		}

		if (p2pContext.signaler && p2pContext.channelName) {
			const channel = p2pContext.signaler.channel(p2pContext.channelName);
			channel.presence.unsubscribe();
			channel.detach();
			p2pManager.closeSignaler();
		}

		if (statsIntervalRef.current) {
			clearInterval(statsIntervalRef.current);
		}
	});

	useEffect(
		function () {
			peerConnectionRef.current = peerConnecion;
		},
		[peerConnecion]
	);

	useEffect(() => {
		// start the signaler when Live View is started
		if (open) {
			p2pManager.initSignaler();
		}
	}, [open]);

	useEffect(
		function () {
			// init P2P
			if (open && !peerConnectionRef.current && p2pContext.ready && !hasSentInitEvent) {
				const connectionStartTime = new Date().getTime();
				setConnectionStartTime(connectionStartTime);

				const connectionId = p2pManager.signalerActiveUsername;

				// append active class teacher username to distinguish connections (laptop agents may have multiple connections
				// to the same channel due to Live View running in a separate process).
				const connectionPeerId = peerType !== "laptop" ? "" : `_${connectionId}`;
				const connectionUsername = `${peer.username}_peerAgentId_${peerAgentId}${connectionPeerId}`;

				p2pManager.sendP2PInfo([
					{
						id: peer.username,
						peerAgentId: peerAgentId,
						connectionPeerId: connectionPeerId,
					},
				]);

				setHasSentInitEvent(true);

				// Moved the iniSignaler to P2PManager when class is started
				const signalerChannel = p2pContext.signaler.channel(p2pContext.channelName);
				signalerChannel.presence.subscribe("present", (member) => {
					if (member.clientId === connectionUsername) {
						console.debug(`${logPrefix} Live View - student ${connectionUsername} 'present' event on presence channel`);
						datadogLogs.logger.info(`${logPrefix} Live View - student ${connectionUsername} 'present' event on presence channel`);
						onPeerInSignaller(connectionStartTime, connectionUsername, connectionId);
					}
				});
				signalerChannel.presence.subscribe("enter", (member) => {
					if (member.clientId === connectionUsername) {
						studentSignallingEstablishedRef.current = true;
						console.debug(`${logPrefix} Live View - student ${connectionUsername} 'enter' event on presence channel`);
						datadogLogs.logger.info(`${logPrefix} Live View - student ${connectionUsername} 'enter' event on presence channel`);
						onPeerInSignaller(connectionStartTime, connectionUsername, connectionId);
					}
				});
				signalerChannel.presence.subscribe("update", (member) => {
					if (member.clientId === connectionUsername) {
						onPeerInSignaller(connectionStartTime, connectionUsername, connectionId);
					}
				});
				signalerChannel.presence.subscribe("leave", (member) => {
					if (member.clientId === connectionUsername) {
						console.debug(`Error: ${logPrefix} Live View - student 'leave' event on presence channel`);
						datadogLogs.logger.error(`${logPrefix} Live View - student 'leave' event on presence channel`);
					}
				});

				// use fallback if the student doesn't signal within 5 seconds
				fallbackTimeoutRef.current = setTimeout(() => {
					if (!studentSignallingEstablishedRef.current) {
						onFallback("STUDENT_NOT_SIGNALED");
					}
				}, 5000);

				// use fallback if the webRTC connection is not established within 10 seconds
				fallbackTimeoutRef.current = setTimeout(() => {
					if (!peerConnectionRef.current) {
						onFallback("CONNECTION_NOT_ESTABLISHED");
					}
				}, 10000);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[open, p2pContext.ready, p2pContext.signaler]
	);

	const onPeerInSignaller = (connectionStartTime, connectionUsername, connectionId) => {
		// when the student has connected to the signaler, initialise the peer connection
		if (!peerConnectionRef.current) {
			console.debug("Peer present in signaller, starting connection.");

			if (fallbackTimeoutRef.current != null) {
				clearTimeout(fallbackTimeoutRef.current);
				fallbackTimeoutRef.current = null;
			}

			let newPeerConnection = new P2PUtils.PeerConnection(
				p2pContext.signaler.channel(p2pContext.channelName),
				p2pContext.ice,
				false,
				connectionId
			);

			newPeerConnection.connect(connectionUsername);
			setPeerConnection(newPeerConnection);

			const pcConnectionStateUpdate = () => {
				if (!peerConnectionRef.current) {
					// this shouldn't happen, safety first
					return;
				}

				switch (newPeerConnection.pc.connectionState) {
					case "failed":
						setError("Connection failed, please try reopening Live View");
						break;
					case "closed":
						setError("Connection unexpectedly closed, please try reopening Live View");
						break;
					default:
						break;
				}
			};

			newPeerConnection.pc.addEventListener("connectionstatechange", pcConnectionStateUpdate);

			const bitrateDC = newPeerConnection.pc.createDataChannel("bitrate");
			let previousValue = 0;
			const bitrateQueue = [];
			const BITRATE_SAMPLE_SIZE = 12;
			const interval = setInterval(() => {
				newPeerConnection.pc.getStats().then((stats) => {
					for (const { type, state, availableIncomingBitrate } of stats.values()) {
						if (type === "candidate-pair" && state === "succeeded" && availableIncomingBitrate !== undefined) {
							// send an initial value
							if (bitrateQueue.length === 0) {
								previousValue = availableIncomingBitrate;
								bitrateDC.send(JSON.stringify(Math.floor(availableIncomingBitrate).toString()));
							}

							bitrateQueue.push(availableIncomingBitrate);
							if (bitrateQueue.length > BITRATE_SAMPLE_SIZE) bitrateQueue.shift();

							// calculate the rolling average, and the absolute difference since last check
							const rollingAverage = bitrateQueue.reduce((sum, a) => sum + a, 0) / bitrateQueue.length;

							// wait for a full sample before sending any messages
							if (bitrateQueue < BITRATE_SAMPLE_SIZE) {
								previousValue = rollingAverage;
								return;
							}

							const diff = Math.abs(previousValue - rollingAverage);

							console.debug("[Live View]: rollingAverageBitrage: " + rollingAverage);
							console.debug("[Live View]: previousAverageBitrate: " + previousValue);
							console.debug("[Live View]: diff: " + diff);
							console.debug("[Live View]: percentage drift: " + (diff / previousValue) * 100);

							// if the difference is more than 20%, then we send the new rolling average
							if (diff / previousValue > 0.2) {
								previousValue = rollingAverage;
								// send the available incoming bitrate to the remote peer
								// so they can adjust the incoming data / compression / resolution
								// accordingly
								bitrateDC.send(JSON.stringify(Math.floor(rollingAverage).toString()));
							}
						}
					}
				});
			}, 2500);

			statsIntervalRef.current = interval;

			statsDatachannelCloseRef.current = () => {
				bitrateDC.close();
				statsDatachannelCloseRef.current = null;
			};

			peerConnectionCloseRef.current = () => {
				peerConnectionRef.current.pc.removeEventListener("connectionstatechange", pcConnectionStateUpdate);
				peerConnectionRef.current.close();
				peerConnectionCloseRef.current = null;

				setPeerConnection(null);
				peerConnectionRef.current = null;
				studentSignallingEstablishedRef.current = false;

				newPeerConnection = null;
			};
		}
	};

	return {
		peerConnection: peerConnecion,
		connectionStartTime: connectionStartTime,
		error: error,
	};
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useChat(classroom, user) {
	const selectedThreadKey = useStore.getState().chat.getThreadKey(classroom, user);
	const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
	const [isChatEnabled, setIsChatEnabled] = useState(false);

	useEffect(() => {
		const chatEnabled = isChatFeatureEnabled();
		setIsChatEnabled(chatEnabled);
		if (chatEnabled) {
			const classroomIdForChat = getClassroomIdForChat(classroom);
			useStore.getState().chat.fetchClassOverview({
				classroomId: classroomIdForChat,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		setHasUnreadMessages(
			isChatEnabled && useStore.getState().chat.unreadMessagesCount.data[selectedThreadKey]
				? useStore.getState().chat.unreadMessagesCount.data[selectedThreadKey].hasUnreadMessages
				: false
		);
		const unsubMessageOverview = useStore.subscribe(
			(state) => state.chat.unreadMessagesCount,
			(newState) => {
				if (newState.data && newState.data[selectedThreadKey]) {
					setHasUnreadMessages(newState.data[selectedThreadKey].hasUnreadMessages);
				}
			}
		);

		return () => {
			unsubMessageOverview();
		};
	}, [selectedThreadKey, isChatEnabled]);

	return {
		hasUnreadMessages: hasUnreadMessages,
		isChatEnabled: isChatEnabled,
		selectedThreadKey: selectedThreadKey,
	};
}
